import { Alert, Layout as AntLayout } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { keys } from 'lodash-es';
import { memo, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, Outlet, matchPath, useLocation } from 'react-router';
import { useRootMemoSelector } from 'use-redux-states';
import logo from '../../assets/images/logo.png';
import appConfig from '../../constants/app';
import routes from '../../constants/routes';
import Company from '../../models/company';
import { setEnforceOtpConfig } from '../../store/app';
import { useFeatureStore } from '../../zustand/features';
import { getOtps } from '../../utils/api/otp';
import { useUser } from '../../utils/hooks/user';
import { refreshAuthToken } from '../../api';
import Forbidden from '../Error/Forbidden';
import SideBar from '../SideBar';
import Header from '../common/Header';
import Loader from '../Loader';

const { Header: AntHeader, Footer } = AntLayout;

export const Layout = memo((props) => {
  const dispatch = useDispatch();
  const comapnyModel = useMemo(() => new Company(), []);
  const is_2fa_enabled_globally = comapnyModel.useStore(
    (company) => company?.options?.is_2fa_enabled
  );
  const location = useLocation();
  const token = useRootMemoSelector('auth.token');
  const refreshToken = useRootMemoSelector('auth.refreshToken');
  const companyInit = useRootMemoSelector('app.companyInit');
  const enforceOtpConfig = useRootMemoSelector('app.enforceOtpConfig');
  const userRoles = useUser((user) => user?.roles);
  const userIdentity = useUser((user) => user?.user?.id);
  const [profileRecoveryAttempted, setProfileRecoveryAttempted] = useState(false);
  const profileRecoveryStarted = useRef(false);

  useEffect(() => {
    if (!token || !refreshToken || (Array.isArray(userRoles) && userIdentity) || profileRecoveryStarted.current) return;

    profileRecoveryStarted.current = true;
    refreshAuthToken()
      .catch(() => null)
      .finally(() => setProfileRecoveryAttempted(true));
  }, [token, refreshToken, userRoles, userIdentity]);
  const featureSyncStatus = useFeatureStore((state) => state?.syncStatus ?? 'unknown');
  const featurePolicyMessage =
    featureSyncStatus === 'degraded'
      ? 'Feature settings are temporarily using the last saved policy while Central Admin is unavailable.'
      : featureSyncStatus === 'unverified'
        ? 'Feature settings are using the baseline policy because no verified Admin snapshot is available.'
        : null;
  const pathRoute = useMemo(
    // find current pathname route
    () =>
      routes[
        keys(routes).find(
          (route) =>
            !!matchPath(
              {
                path: routes[route].pathname,
                exact: false,
                strict: false
              },
              location.pathname
            )
        )
      ],
    [location.pathname]
  );

  useEffect(() => {
    if (is_2fa_enabled_globally) {
      getOtps()
        .then(({ data: { data: otps } }) => {
          const configured = !!otps?.find(
            ({ method, is_setup }) => ['authenticator', 'sms', 'email'].includes(method) && is_setup
          );

          if (!configured) {
            dispatch(setEnforceOtpConfig(true));
          }
        })
        .catch(console.log);
    }
  }, [is_2fa_enabled_globally]);

  const acceptedRoles = useMemo(() => pathRoute?.roles, [pathRoute?.roles]);

  if (enforceOtpConfig && location.pathname !== routes.TWOFA.pathname) {
    return <Navigate to={routes.TWOFA.pathname} />;
  }

  if (companyInit === false && location.pathname !== routes.SETUP.pathname) {
    return <Navigate to={routes.SETUP.pathname} />;
  }

  if (!token) {
    return <Navigate to={routes.LOGIN.pathname} />;
  }

  if (refreshToken && (!Array.isArray(userRoles) || !userIdentity) && !profileRecoveryAttempted) {
    return <Loader />;
  }

  // Deny-by-default: Only allow if acceptedRoles is defined and user has an authorized role
  if (
    !acceptedRoles ||
    acceptedRoles.length === 0 ||
    !userRoles?.some((userRole) => acceptedRoles.includes(userRole))
  ) {
    return <Forbidden />;
  }

  return (
    <AntLayout className='flex flex-col min-h-screen'>
      <SideBar />
      <AntLayout>
        <Header />
        {featurePolicyMessage && (
          <div className='mt-16 mx-auto w-[calc(100%-2rem)] max-w-[1200px]'>
            <Alert type='warning' showIcon message={featurePolicyMessage} />
          </div>
        )}
        <Content
          className={`${featurePolicyMessage ? 'mt-0' : 'mt-16'} p-0 min-h-[calc(100vh-64px)]`}>
          <SuspenseLayout />
        </Content>
      </AntLayout>
    </AntLayout>
  );
});

export const AuthLayout = memo(() => {
  const footerLinks = useMemo(
    () => [
      {
        text: 'Contact Us',
        link: 'https://sawport-team-d19g630i.atlassian.net/servicedesk/customer/portals'
      },
      { text: 'Privacy Policy', link: 'https://sawport.com/privacy-policy/' },
      {
        text: "Can't login, visit our help center",
        link: 'https://sawport-team-d19g630i.atlassian.net/servicedesk/customer/portal/1/group/3'
      }
    ],
    []
  );

  return (
    <AntLayout className='flex min-h-svh flex-col'>
      <AntHeader
        style={{
          display: 'flex',
          alignItems: 'center',
          background: '#fff',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          width: '100%'
        }}>
        <img src={logo} alt='logo' className='w-[100px]' />
      </AntHeader>
      <Content className='flex flex-col justify-center my-16'>
        <SuspenseLayout />
      </Content>
      <Footer className='flex bg-white flex-col sm:flex-row justify-between items-center w-full text-[12px]'>
        <div className='flex items-center'>
          <p className='my-1'>Version {appConfig.version}</p>
          <p className='my-1 ml-2'>
            Powered by <img src={logo} alt='logo' className='w-[65px]' />
          </p>
        </div>
        <div className='flex flex-wrap items-center justify-center gap-2'>
          {footerLinks.map(({ text, link }, index) => (
            <div
              key={index}
              className='ml-2 cursor-pointer text-[#43425d] pr-2 whitespace-nowrap'
              style={{ borderRight: index !== footerLinks.length - 1 ? '1px solid #CED4DA' : '' }}>
              <a
                target='_blank'
                rel='noreferrer noopener'
                className='ml-2 cursor-pointer text-[#43425d] pr-2'
                href={link}
                style={{ lineHeight: '5px', paddingBottom: '2px', display: 'inline-block' }}>
                {text}
              </a>
            </div>
          ))}
        </div>
        <p className='my-1'>
          Copyright © - {new Date().getFullYear()} Sawport Inc. All rights reserved
        </p>
      </Footer>
    </AntLayout>
  );
});

export function SuspenseLayout() {
  return (
    <Suspense fallback={<Loader />}>
      <Outlet />
    </Suspense>
  );
}
